import React, { useState, useRef, useEffect, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { authStyles } from "./auth-styles";

/**
 * Shared portal login screen (admin + client portals).
 *
 * Login phases:
 *   "idle"       – form visible
 *   "signing-in" – spinner replaces form (waiting for auth response)
 *   "shrinking"  – card shrinks to 4rem circle around spinner
 *
 * When shrinking completes, onTransitionDone fires and the parent takes
 * over (e.g. the admin portal's clip-path reveal). Parents without a
 * reveal transition simply omit signedIn/onTransitionDone and swap the
 * screen out once their auth state changes.
 */

type Phase = "idle" | "signing-in" | "shrinking" | "expanding";

export interface LoginScreenProps {
  /** Heading above the form, e.g. "Admin Portal" / "Client Portal". */
  title: string;
  onSignIn: (email: string, password: string) => Promise<void> | void;
  error?: React.ReactNode;
  signedIn?: boolean;
  onTransitionDone?: () => void;
}

export function LoginScreen({ title, onSignIn, error, signedIn, onTransitionDone }: LoginScreenProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // A screen mounted already-signed-in is the reveal handoff: the submitted
  // screen already shrank to the circle, so this one renders *pre-collapsed*
  // and hands straight to the reveal (which continues the iris from that same
  // circle) — replaying the shrink here is what caused it to run twice.
  const [phase, setPhase] = useState<Phase>(signedIn ? "shrinking" : "idle");
  const [loginError, setLoginError] = useState<React.ReactNode>(null);
  const [cardHeight, setCardHeight] = useState<number | undefined>(undefined);
  const [shrinkComplete, setShrinkComplete] = useState(!!signedIn);
  const cardRef = useRef<HTMLDivElement>(null);

  // When parent reports an error, snap back to form
  useEffect(() => {
    if (error) {
      setLoginError(error);
      setPhase((currentPhase) => {
        if (currentPhase === "shrinking" || currentPhase === "signing-in") {
          return "expanding";
        }
        setCardHeight(undefined);
        return "idle";
      });
    }
  }, [error]);

  // When parent confirms sign-in succeeded and shrink is done, tell parent
  useEffect(() => {
    if (signedIn && shrinkComplete) {
      onTransitionDone?.();
    }
  }, [signedIn, shrinkComplete, onTransitionDone]);

  // Start the shrink animation immediately after submit (signing-in phase)
  useEffect(() => {
    if (phase === "signing-in") {
      // Kick off the shrink on the next frame so the measured height is applied first
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPhase("shrinking");
        });
      });
    }
  }, [phase]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setShrinkComplete(false);

    // Measure height before hiding the form so we get its full natural height
    if (cardRef.current) {
      setCardHeight(cardRef.current.getBoundingClientRect().height);
    }

    setPhase("signing-in");

    try {
      await onSignIn(email, password);
    } catch {
      setPhase("expanding");
    }
  };

  const handleTransitionEnd = useCallback((e: React.TransitionEvent) => {
    // Only react to transitions on the card itself, not children
    if (e.target !== cardRef.current) return;
    if (phase === "shrinking") {
      setShrinkComplete(true);
    } else if (phase === "expanding") {
      setPhase("idle");
      setCardHeight(undefined);
    }
  }, [phase]);

  const isForm = phase === "idle";
  const showSpinner = phase === "signing-in" || phase === "shrinking";

  // Inline height: set during signing-in, shrinking, and expanding
  const cardStyle: React.CSSProperties = {};
  if (cardHeight !== undefined) {
    cardStyle.height = `${cardHeight}px`;
  }

  return (
    <div className="login-backdrop">
      <style>{authStyles}</style>

      <div
        ref={cardRef}
        className={`login-card ${phase}`}
        style={cardStyle}
        onTransitionEnd={handleTransitionEnd}
      >
        {/* ── Form ── */}
        <div className={`login-form-wrap ${isForm ? "visible" : "hidden"}`}>
          <h1 className="login-title">{title}</h1>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field">
              <Label htmlFor="login-email" className="login-label">Email</Label>
              <Input
                id="login-email"
                type="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                autoComplete="email"
                className="login-input"
              />
            </div>
            <div className="login-field">
              <Label htmlFor="login-password" className="login-label">Password</Label>
              <Input
                id="login-password"
                type="password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                autoComplete="current-password"
                className="login-input"
              />
            </div>
            <Button type="submit" className="login-button">
              Sign in
            </Button>
            {loginError && <p className="login-error">{loginError}</p>}
          </form>
        </div>

        {/* ── Spinner ── */}
        {showSpinner && (
          <div className="login-spinner-wrap">
            <Loader2 className="login-spinner" />
          </div>
        )}
      </div>
    </div>
  );
}
